/**
 * Mobile Menu Handler
 * Manages the mobile menu and search overlay functionality including opening, closing, and animations.
 * Features scroll-based header visibility and menu positioning.
 */

export class MobileMenu {
    constructor() {
        // Initialize DOM elements
        this.menuToggle = document.getElementById('mobile-menu-toggle');
        this.menuPanel = document.getElementById('mobile-menu');
        this.searchToggle = document.getElementById('search-toggle');
        this.searchPanel = document.getElementById('mobile-search');
        this.header = document.querySelector('.site-header');
        
        // State management
        this.isOpen = false;
        this.isSearchOpen = false;
        this.lastScrollY = window.scrollY;
        this.scrollThreshold = 5;

        if (this.menuToggle && this.menuPanel && this.header) {
            this.initialize();
        }
    }

    initialize() {
        this.setupMenuPosition();
        this.addEventListeners();
        this.initializeScrollHandler();
        
        if (this.searchToggle && this.searchPanel) {
            this.initializeSearch();
        }
    }

    setupMenuPosition() {
        // Set CSS variable for header height
        const headerHeight = this.header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        
        // Ensure menu and search panels are properly positioned
        this.menuPanel.style.height = `calc(100vh - ${headerHeight}px)`;
        if (this.searchPanel) {
            this.searchPanel.style.height = `calc(100vh - ${headerHeight}px)`;
        }
    }

    initializeScrollHandler() {
        let ticking = false;

        window.addEventListener('scroll', () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    this.handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        });

        // Handle viewport and orientation changes
        window.addEventListener('resize', () => {
            this.setupMenuPosition();
        });

        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.setupMenuPosition();
            }, 100);
        });
    }

    handleScroll() {
        // Skip scroll handling if menu or search is open
        if (this.isOpen || this.isSearchOpen) {
            return;
        }

        const currentScrollY = window.scrollY;
        const scrollingDown = currentScrollY > this.lastScrollY;
        const scrollDelta = Math.abs(currentScrollY - this.lastScrollY);

        if (scrollDelta > this.scrollThreshold) {
            if (scrollingDown && currentScrollY > 0) {
                this.header.classList.add('header-hidden');
            } else {
                this.header.classList.remove('header-hidden');
            }
        }

        this.lastScrollY = currentScrollY;
    }

    addEventListeners() {
        // Toggle menu on button click
        this.menuToggle.addEventListener('click', () => {
            if (this.isSearchOpen) {
                this.closeSearch();
            }
            this.toggleMenu();
        });

        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
            if (this.isOpen &&
                !this.menuPanel.contains(e.target) &&
                !this.menuToggle.contains(e.target)) {
                this.closeMenu();
            }
        });

        // Handle keyboard accessibility
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (this.isOpen) {
                    this.closeMenu();
                }
                if (this.isSearchOpen) {
                    this.closeSearch();
                }
            }
        });
    }

    initializeSearch() {
        this.searchToggle.addEventListener('click', () => {
            if (this.isOpen) {
                this.closeMenu();
            }
            this.toggleSearch();
        });

        document.addEventListener('click', (e) => {
            if (this.isSearchOpen &&
                !this.searchPanel.contains(e.target) &&
                !this.searchToggle.contains(e.target)) {
                this.closeSearch();
            }
        });
    }

    toggleMenu() {
        if (this.isOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    toggleSearch() {
        if (this.isSearchOpen) {
            this.closeSearch();
        } else {
            this.openSearch();
        }
    }

    openMenu() {
        this.isOpen = true;
        this.menuToggle.setAttribute('aria-expanded', 'true');
        this.menuToggle.classList.add('is-active');
        this.menuPanel.classList.remove('-translate-x-full');
        this.menuPanel.classList.add('is-active');
        this.menuPanel.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.header.classList.remove('header-hidden');
    }

    closeMenu() {
        this.isOpen = false;
        this.menuToggle.setAttribute('aria-expanded', 'false');
        this.menuToggle.classList.remove('is-active');
        this.menuPanel.classList.add('-translate-x-full');
        this.menuPanel.classList.remove('is-active');
        this.menuPanel.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }

    openSearch() {
        this.isSearchOpen = true;
        this.searchToggle.setAttribute('aria-expanded', 'true');
        this.searchToggle.classList.add('is-active');
        this.searchPanel.classList.remove('-translate-x-full');
        this.searchPanel.classList.add('is-active');
        this.searchPanel.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.header.classList.remove('header-hidden');

        // Focus the search input for immediate typing
        const searchInput = this.searchPanel.querySelector('input[type="search"]');
        if (searchInput) {
            searchInput.focus();
        }
    }

    closeSearch() {
        this.isSearchOpen = false;
        this.searchToggle.setAttribute('aria-expanded', 'false');
        this.searchToggle.classList.remove('is-active');
        this.searchPanel.classList.add('-translate-x-full');
        this.searchPanel.classList.remove('is-active');
        this.searchPanel.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }
}

export default MobileMenu;